import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextInput } from "../../../FlowConfigurationInputs/TextInput/NewTextInput";
import FieldLabel from '../../../FlowConfigurationInputs/FieldLabel/FieldLabel';
import VariablePicker from "../../../FlowConfigurationInputs/VariablePicker/VariablePicker";
import { alertState, changedFields, initialActions, saveAvailable } from "../../../../recoil-store/recoil-state";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

// Import Material UI
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import MoreVertIcon from '@material-ui/icons/MoreVert';

// Import Helpers
import { checkTheDifference, addDifference, removeDifference, CheckIfFlowUpdated } from '../../../../helpers/helper';
import FilterModal from "../../../FlowConfigurationInputs/FilterModal/FilterModal";

// Import CSS
import "./Search.css"

/**
 * @Description this page Object  Library's Assign Action's configuration form
 * @Param       action - object
 * @Param       variables - object variable list
 * @Param       setOpenVariable - open variable function that allows us to open
 * @Param       variableRenbder - variable render
 * @param       process -  boolean
 * @Returns     div element
 * @Author      Emirhan Bilal Yeşil
 */
export default function Search({ action, variables, setOpenVariable, automId, automVersionId, automDetail, variableRender, process }) {
    const { t } = useTranslation();
    const [openAdd, setOpenAdd] = useState(false);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [conditions, setConditions] = useState([]);
    const [isOk, setIsOk] = useState(false);
    const [currentCondition, setCurrentCondition] = useState({});
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);
    const setAlert = useSetRecoilState(alertState);
    const selectedIndex = useRef(0);
    const conditionInfo = useRef({});
    const selectedCondition = useRef(null);
    const label = "CONTENT.CONFIGURATION-FORM.OBJECT.SEARCH.";

    useEffect(() => {
        if (!action.fields.conditions) { action.fields.conditions = { value: [], type: 'CONDITION' }; setConditions([]); }
        else { setConditions(action.fields.conditions.value); }
    }, [action]);

    const handleAddClick = (e) => {
        conditionInfo.current = { operator: 'eq', fields: { key: { value: '' }, value: { value: '' } }, logic: 'last' }
        setOpenAdd(true);
    }

    const handleUpdateClick = (e, index) => {
        setCurrentCondition(conditions[index]);
        selectedIndex.current = index;
        let el = e.target;
        while (el.className !== 'condition-picker') {
            el = el.parentElement;
        }
        el.classList.add('condition-selected');
        selectedCondition.current = el;
        setOpenUpdate(true);
    }
    const checkIsValid = (c) => {
        if (conditionInfo.current.fields.key.value === "" || (["contains", "eq", "ne"].includes(conditionInfo.current.operator) && conditionInfo.current.fields.value?.value === "")) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t("MESSAGES.VALIDATION.REQUIRED-FIELDS-MISSING"),
                severity: 'error'
            }))
            return false
        }
        return true;
    }

    const handleAdd = (e) => {
        if (!checkIsValid(conditionInfo)) {
            return;
        }
        let index = conditions.length - 1;
        if (index > -1) {
            conditions[index].logic = 'and';
        }
        let newConditions = [...conditions, { ...conditionInfo.current }];
        newConditions.conditions = "string"
        action.fields.conditions.value = newConditions;
        setConditions(newConditions);
        changeManagement(newConditions);
        setOpenAdd(false);
    }

    const handleAddNew = (e) => {
        setIsOk((curr) => !curr)
        if (!checkIsValid(conditionInfo)) {
            return;
        }
        let index = conditions.length - 1;
        if (index > -1) {
            conditions[index].logic = 'and';
        }
        let newConditions = [...conditions, { ...conditionInfo.current }];
        newConditions.conditions = "string"
        action.fields.conditions.value = newConditions;
        setConditions(newConditions);
        changeManagement(newConditions);
        conditionInfo.current = { operator: 'eq', fields: { key: { value: '' }, value: { value: '' } }, logic: 'last' }
    }

    const handleUpdate = (e) => {
        if (currentCondition.fields.key.value === "" || (["contains", "eq", "ne"].includes(currentCondition.operator) && currentCondition.fields.value?.value === "")) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t("MESSAGES.VALIDATION.REQUIRED-FIELDS-MISSING"),
                severity: 'error'
            }))
            return false
        }

        changeManagement(conditions);
        closeUpdate();
    }

    const handleDelete = (e) => {
        let n = conditions.length;
        let i = selectedIndex.current;
        if (n > 1 && i === n - 1) {
            conditions[n - 2].logic = 'last';
        }
        conditions.splice(i, 1);
        changeManagement(conditions);
        closeUpdate();
    }

    const closeUpdate = (conditionBackup) => {
        if (conditionBackup) {
            action.fields.conditions.value[selectedIndex.current] = conditionBackup
        }
        selectedCondition.current.classList.remove('condition-selected');
        setOpenUpdate(false);
    }

    const handleLogicChange = (e, index) => {
        let target = e.target;
        for (const el of target.parentNode.childNodes) {
            el.classList.remove('condition-active')
        }
        target.classList.add('condition-active')
        conditions[index].logic = target.value;
        const newValue = conditions;
        const field = initialActionsData[action.id]?.fields["conditions"];
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated("conditions", action, oldValue, newValue, setSaveAvailable, setChangeFields, changedFieldsState);
    }

    const changeManagement = (newValue) => {
        const field = initialActionsData[action.id]?.fields['conditions'];
        const oldValue = !field ? undefined : field.value;
        const cfTemp = JSON.parse(JSON.stringify(changedFieldsState));
        if (!oldValue) {
            addDifference(cfTemp, action, 'conditions', setChangeFields, setSaveAvailable)
        } else {
            let newCondition = [];
            for (const item of newValue) {
                newCondition.push(`${item.fields.key.value}_${item.operator.toUpperCase()}_${item.fields.value.value}`);
            }

            let oldCondition = [];
            for (const item of oldValue) {
                oldCondition.push(`${item.fields.key.value}_${item.operator.toUpperCase()}_${item.fields.value.value}`);
            }

            if (checkTheDifference(newCondition, oldCondition)) {
                addDifference(cfTemp, action, 'conditions', setChangeFields, setSaveAvailable)
            } else {
                removeDifference(cfTemp, action, 'conditions', setChangeFields, setSaveAvailable)
            }
        }
    }


    return (
        <div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + "OBJECT-ARRAY")} is_required={true} />
                <TextInput
                    action={action}
                    inputName="sourceValue"
                    variables={variables}
                    setOpenVariable={setOpenVariable}
                    isRequired
                    multiline={4}
                    fieldType="array"
                    hideRequired
                    displayName={t(label + 'OBJECT-ARRAY')}
                />
                <small className="conf-field-desc"><em>{'e.g. [{"name":"testUser"},{"name":"testUser2"}]'}</em></small>
            </div>

            <div className="conf-field-container">
                {/* <FieldLabel name={t(label + "FILTER")} is_required={true} /> */}
                <div className="form-element form-title">
                    <Button onClick={handleAddClick}
                        style={{ border: "none", color: "#8F94A6", fontWeight: 700, textTransform: "none" }}>
                        {t(label + 'ADD-FILTER')}
                        <AddIcon style={{ paddingLeft: 5 }} onClick={handleAddClick} />
                    </Button>
                </div>
                {conditions.map((condition, conditionI) =>
                    <div key={conditionI}>
                        <div className="form-element">
                            <ObjectSearchFilter condition={condition} subTitle={"Filter"} onClick={(e) => { handleUpdateClick(e, conditionI) }} />
                        </div>
                        {condition.logic !== 'last' &&
                            <div className="form-element" style={{ textAlign: 'center' }}>
                                <div className="condition-tabs">
                                    <button className={condition.logic === 'and' ? 'condition-active' : ''}
                                        value="and" onClick={(e) => { handleLogicChange(e, conditionI) }}>
                                        And
                                    </button>
                                    <button className={condition.logic === 'or' ? 'condition-active' : ''}
                                        value="or" onClick={(e) => { handleLogicChange(e, conditionI) }}>
                                        Or
                                    </button>
                                </div>
                            </div>
                        }
                    </div>
                )}
                {openAdd &&
                    <FilterModal automDetail={automDetail} action={action} conditionInfo={conditionInfo.current}
                        handleAddNew={handleAddNew}
                        setOpen={setOpenAdd} handleAdd={handleAdd}
                        variables={variables} variableRender={variableRender} setOpenVariable={setOpenVariable}
                        process={process} automId={automId} automVersionId={automVersionId} isOk={isOk} />

                }
                {openUpdate &&
                    <FilterModal
                        automDetail={automDetail}
                        action={action}
                        conditionInfo={conditions[selectedIndex.current]}
                        variableRender={variableRender}
                        setOpen={setOpenUpdate} handleUpdate={handleUpdate}
                        handleDelete={handleDelete} onClose={closeUpdate}
                        variables={variables} setOpenVariable={setOpenVariable}
                        process={process} automId={automId} automVersionId={automVersionId} isOk={isOk} />
                }
            </div>

            <div className="conf-field-container">
                <FieldLabel name={t(label + 'ASSIGN')} is_required />
                <VariablePicker action={action} inputName="set" variables={variables} setOpenVariable={setOpenVariable}
                    isRequired hideRequired filter={"array"} />
                <small
                    className="conf-field-desc"><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: 'array' })}</em></small>
            </div>
        </div >
    );
}


function ObjectSearchFilter({ condition, onClick, subTitle }) {
    const { t } = useTranslation();
    const label = 'CONTENT.CONFIGURATION-FORM.CONDITION.';
    return (
        <div className="condition-picker">
            <fieldset>
                <legend>&nbsp;<span>{subTitle}</span>&nbsp;</legend>
                <div>
                    <div>
                        <span className="condition-value">{condition.fields?.key?.value}</span>
                        &nbsp;<span>{t(label + condition.operator.toUpperCase())}</span>&nbsp;
                        <span className="condition-value">{condition.fields?.value?.value}</span>
                    </div>
                    <div>
                        <button onClick={onClick}><MoreVertIcon /></button>
                    </div>
                </div>
            </fieldset>
        </div>
    );
}
